export const CRM_TASK_TYPE_VALUES = [
  "call",
  "email",
  "meeting",
  "follow_up",
  "scheduled_call",
  "lead_qualification",
  "customer_reply_followup",
  "consultation_followup",
  "offer_creation",
  "offer_followup",
  "proposal_revival",
  "promise_followup",
  "payment_followup",
  "payment_verification",
  "invoice_collection",
  "plan_classification",
  "renewal",
  "subscription_renewal",
  "engagement_check",
  "cadence_outreach",
  "winback",
  "churn_prevention",
  "break_return_check",
  "no_show_followup",
  "support_ticket",
  "email_bounce_followup",
  "deal_revival",
  "review",
  "review_recovery",
  "feedback_milestone",
  "referral_followup",
  "learning_plateau_intervention",
  "learning_streak_recovery",
  "custom",
] as const;
export type CrmTaskType = (typeof CRM_TASK_TYPE_VALUES)[number];

export const CRM_TASK_TYPE_ALIASES = {
  appointment: "scheduled_call",
  balance_check: "payment_verification",
  callback: "scheduled_call",
  check_balance_add: "payment_verification",
  check_installment_payment: "payment_verification",
  check_payment: "payment_verification",
  clasifica_plan: "plan_classification",
  clasificare_plan: "plan_classification",
  consultation: "consultation_followup",
  cognitive_dissonance: "engagement_check",
  create_offer: "offer_creation",
  csat_check: "feedback_milestone",
  customer_reply: "customer_reply_followup",
  failed_payment: "payment_followup",
  follow_up_call: "scheduled_call",
  follow_up_failed_payment: "payment_followup",
  lead_call: "lead_qualification",
  lead_followup: "lead_qualification",
  monitor: "engagement_check",
  nurture: "cadence_outreach",
  new_qualified_lead: "lead_qualification",
  nps_milestone: "feedback_milestone",
  offer_expired_winback: "proposal_revival",
  offer_followup_call: "offer_followup",
  offer_followup_final: "offer_followup",
  offer_followup_initial: "offer_followup",
  offer_promise_date: "promise_followup",
  offer_promise_missed: "promise_followup",
  other: "custom",
  payment_check: "payment_verification",
  payment_failed_followup: "payment_followup",
  reactivation: "winback",
  review_duplicate: "review",
  renewal_preemptive: "renewal",
  renewal_standard: "renewal",
  renewal_urgent: "renewal",
  reply_followup: "customer_reply_followup",
  revenire_intarziata: "break_return_check",
  rhythm_adherence: "engagement_check",
  schedule_lesson: "meeting",
  schedule_trial_lesson: "meeting",
  score_crossing: "lead_qualification",
  send_offer: "offer_followup",
  support_case: "support_ticket",
  support_followup: "support_ticket",
  ticket_followup: "support_ticket",
  verificare_pauza: "break_return_check",
  verify_installment: "payment_verification",
  verify_offer: "offer_followup",
  winback_urgent: "winback",
} as const satisfies Record<string, CrmTaskType>;
export type CrmTaskTypeAlias = keyof typeof CRM_TASK_TYPE_ALIASES;

export const CRM_TASK_PRIORITY_VALUES = ["low", "medium", "high", "urgent"] as const;
export type CrmTaskPriority = (typeof CRM_TASK_PRIORITY_VALUES)[number];

export const CRM_TASK_STATUS_VALUES = ["pending", "in_progress", "completed", "skipped", "cancelled"] as const;
export type CrmTaskStatus = (typeof CRM_TASK_STATUS_VALUES)[number];

export const CRM_TASK_COMPLETION_OUTCOME_VALUES = [
  "completed",
  "auto_completed",
  "connected",
  "customer_replied",
  "customer_renewed",
  "customer_declined",
  "callback_requested",
  "payment_received",
  "rescheduled",
  "no_answer",
  "not_interested",
  "cancelled",
  "escalated",
] as const;
export type CrmTaskCompletionOutcome = (typeof CRM_TASK_COMPLETION_OUTCOME_VALUES)[number];

export type CrmTaskCategory =
  | "communication"
  | "sales"
  | "offer"
  | "payment"
  | "retention"
  | "service"
  | "quality"
  | "learning"
  | "coordination"
  | "custom";

export interface CrmTaskOutcomeDefinition {
  value: CrmTaskCompletionOutcome;
  label: string;
  requiresNotes?: boolean;
  suggestsFollowUp?: boolean;
}

export interface CrmTaskCompletionFieldOption {
  value: string;
  label: string;
}

export interface CrmTaskCompletionFieldDefinition {
  key: string;
  label: string;
  type: "select" | "text" | "date";
  required?: boolean;
  requiredOutcomes?: readonly CrmTaskCompletionOutcome[];
  options?: readonly CrmTaskCompletionFieldOption[];
}

export interface CrmTaskRegistryEntry {
  type: CrmTaskType;
  label: string;
  category: CrmTaskCategory;
  defaultPriority: CrmTaskPriority;
  manualCreatable: boolean;
  defaultFollowUpType: CrmTaskType;
  completionOutcomes: readonly CrmTaskCompletionOutcome[];
  completionFields?: readonly CrmTaskCompletionFieldDefinition[];
}

export const CRM_TASK_OUTCOME_DEFINITIONS: Record<CrmTaskCompletionOutcome, CrmTaskOutcomeDefinition> = {
  completed: { value: "completed", label: "Completed" },
  auto_completed: { value: "auto_completed", label: "Auto-completed" },
  connected: { value: "connected", label: "Connected" },
  customer_replied: { value: "customer_replied", label: "Customer replied", suggestsFollowUp: true },
  customer_renewed: { value: "customer_renewed", label: "Customer renewed" },
  customer_declined: { value: "customer_declined", label: "Customer declined", requiresNotes: true },
  callback_requested: { value: "callback_requested", label: "Callback requested", suggestsFollowUp: true },
  payment_received: { value: "payment_received", label: "Payment received" },
  rescheduled: { value: "rescheduled", label: "Rescheduled", suggestsFollowUp: true },
  no_answer: { value: "no_answer", label: "No answer", requiresNotes: true, suggestsFollowUp: true },
  not_interested: { value: "not_interested", label: "Not interested", requiresNotes: true },
  cancelled: { value: "cancelled", label: "Cancelled", requiresNotes: true },
  escalated: { value: "escalated", label: "Escalated", requiresNotes: true, suggestsFollowUp: true },
};

const COMMUNICATION_OUTCOMES = [
  "completed",
  "connected",
  "customer_replied",
  "callback_requested",
  "rescheduled",
  "no_answer",
  "not_interested",
] as const satisfies readonly CrmTaskCompletionOutcome[];

const OFFER_OUTCOMES = [
  "completed",
  "connected",
  "customer_replied",
  "payment_received",
  "callback_requested",
  "rescheduled",
  "no_answer",
  "customer_declined",
] as const satisfies readonly CrmTaskCompletionOutcome[];

const RETENTION_OUTCOMES = [
  "completed",
  "connected",
  "customer_renewed",
  "customer_declined",
  "callback_requested",
  "rescheduled",
  "no_answer",
] as const satisfies readonly CrmTaskCompletionOutcome[];

const SERVICE_OUTCOMES = [
  "completed",
  "connected",
  "rescheduled",
  "customer_replied",
  "no_answer",
  "escalated",
] as const satisfies readonly CrmTaskCompletionOutcome[];

const CALL_COMPLETION_FIELDS = [
  {
    key: "callOutcome",
    label: "Call outcome",
    type: "select",
    required: true,
    options: [
      { value: "reached", label: "Reached" },
      { value: "voicemail", label: "Voicemail" },
      { value: "no_answer", label: "No answer" },
      { value: "wrong_contact", label: "Wrong contact" },
    ],
  },
  {
    key: "customerResponse",
    label: "Customer response",
    type: "select",
    options: [
      { value: "ready_to_buy", label: "Ready to buy" },
      { value: "considering", label: "Considering" },
      { value: "has_concerns", label: "Has concerns" },
      { value: "not_interested", label: "Not interested" },
      { value: "postponed", label: "Postponed" },
    ],
  },
] as const satisfies readonly CrmTaskCompletionFieldDefinition[];

const OFFER_COMPLETION_FIELDS = [
  ...CALL_COMPLETION_FIELDS,
  {
    key: "staffConfidence",
    label: "Staff confidence",
    type: "select",
    options: [
      { value: "high", label: "High" },
      { value: "medium", label: "Medium" },
      { value: "low", label: "Low" },
      { value: "unknown", label: "Unknown" },
    ],
  },
  {
    key: "commitmentLevel",
    label: "Commitment level",
    type: "select",
    options: [
      { value: "none", label: "No commitment" },
      { value: "interested", label: "Interested" },
      { value: "verbal_commit", label: "Verbal commitment" },
      { value: "date_given", label: "Payment date given" },
    ],
  },
  {
    key: "declineCategory",
    label: "Decline category",
    type: "select",
    requiredOutcomes: ["customer_declined", "not_interested"],
    options: [
      { value: "price", label: "Price" },
      { value: "timing", label: "Timing" },
      { value: "fit", label: "Fit" },
      { value: "competitor", label: "Competitor" },
      { value: "other", label: "Other" },
    ],
  },
] as const satisfies readonly CrmTaskCompletionFieldDefinition[];

const RETENTION_COMPLETION_FIELDS = [
  ...CALL_COMPLETION_FIELDS,
  {
    key: "staffConfidence",
    label: "Staff confidence",
    type: "select",
    options: [
      { value: "high", label: "High" },
      { value: "medium", label: "Medium" },
      { value: "low", label: "Low" },
      { value: "unknown", label: "Unknown" },
    ],
  },
  {
    key: "renewalConfidence",
    label: "Renewal confidence",
    type: "select",
    options: [
      { value: "high", label: "High" },
      { value: "medium", label: "Medium" },
      { value: "low", label: "Low" },
      { value: "lost", label: "Likely lost" },
    ],
  },
] as const satisfies readonly CrmTaskCompletionFieldDefinition[];

const BREAK_RETURN_COMPLETION_FIELDS = [
  ...RETENTION_COMPLETION_FIELDS,
  {
    key: "expectedReturnDate",
    label: "Expected return date",
    type: "date",
    requiredOutcomes: ["rescheduled", "callback_requested"],
  },
] as const satisfies readonly CrmTaskCompletionFieldDefinition[];

const SERVICE_COMPLETION_FIELDS = [
  {
    key: "serviceRecoveryOutcome",
    label: "Recovery outcome",
    type: "select",
    options: [
      { value: "resolved", label: "Resolved" },
      { value: "rescheduled", label: "Rescheduled" },
      { value: "still_at_risk", label: "Still at risk" },
      { value: "escalated", label: "Escalated" },
    ],
  },
] as const satisfies readonly CrmTaskCompletionFieldDefinition[];

const PAYMENT_VERIFICATION_FIELDS = [
  ...OFFER_COMPLETION_FIELDS,
  {
    key: "paymentState",
    label: "Payment state",
    type: "select",
    options: [
      { value: "paid", label: "Paid" },
      { value: "pending", label: "Pending" },
      { value: "promised", label: "Promised" },
      { value: "failed", label: "Failed" },
      { value: "disputed", label: "Disputed" },
      { value: "unverified", label: "Unverified" },
    ],
  },
  {
    key: "paymentReference",
    label: "Payment reference",
    type: "text",
    requiredOutcomes: ["payment_received"],
  },
] as const satisfies readonly CrmTaskCompletionFieldDefinition[];

const PLAN_CLASSIFICATION_FIELDS = [
  {
    key: "planClass",
    label: "Plan class",
    type: "select",
    required: true,
    options: [
      { value: "starter", label: "Starter" },
      { value: "growth", label: "Growth" },
      { value: "premium", label: "Premium" },
      { value: "enterprise", label: "Enterprise" },
      { value: "custom", label: "Custom" },
      { value: "not_applicable", label: "Not applicable" },
    ],
  },
  {
    key: "classificationConfidence",
    label: "Classification confidence",
    type: "select",
    options: [
      { value: "high", label: "High" },
      { value: "medium", label: "Medium" },
      { value: "low", label: "Low" },
    ],
  },
] as const satisfies readonly CrmTaskCompletionFieldDefinition[];

const FEEDBACK_COMPLETION_FIELDS = [
  {
    key: "feedbackOutcome",
    label: "Feedback outcome",
    type: "select",
    options: [
      { value: "promoter", label: "Promoter" },
      { value: "neutral", label: "Neutral" },
      { value: "detractor", label: "Detractor" },
      { value: "issue_escalated", label: "Issue escalated" },
      { value: "no_response", label: "No response" },
    ],
  },
] as const satisfies readonly CrmTaskCompletionFieldDefinition[];

const CADENCE_COMPLETION_FIELDS = [
  ...CALL_COMPLETION_FIELDS,
  {
    key: "channelUsed",
    label: "Channel used",
    type: "select",
    options: [
      { value: "call", label: "Call" },
      { value: "email", label: "Email" },
      { value: "sms", label: "SMS" },
      { value: "whatsapp", label: "WhatsApp" },
      { value: "chat", label: "Chat" },
      { value: "other", label: "Other" },
    ],
  },
] as const satisfies readonly CrmTaskCompletionFieldDefinition[];

export const CRM_TASK_REGISTRY: Record<CrmTaskType, CrmTaskRegistryEntry> = {
  call: {
    type: "call",
    label: "Call",
    category: "communication",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "call",
    completionOutcomes: COMMUNICATION_OUTCOMES,
    completionFields: CALL_COMPLETION_FIELDS,
  },
  email: {
    type: "email",
    label: "Email",
    category: "communication",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "email",
    completionOutcomes: COMMUNICATION_OUTCOMES,
  },
  meeting: {
    type: "meeting",
    label: "Meeting",
    category: "coordination",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: ["completed", "connected", "rescheduled", "no_answer"],
  },
  follow_up: {
    type: "follow_up",
    label: "Follow-up",
    category: "communication",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: COMMUNICATION_OUTCOMES,
  },
  scheduled_call: {
    type: "scheduled_call",
    label: "Scheduled call",
    category: "communication",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "call",
    completionOutcomes: COMMUNICATION_OUTCOMES,
    completionFields: CALL_COMPLETION_FIELDS,
  },
  lead_qualification: {
    type: "lead_qualification",
    label: "Lead qualification",
    category: "sales",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: COMMUNICATION_OUTCOMES,
    completionFields: CALL_COMPLETION_FIELDS,
  },
  customer_reply_followup: {
    type: "customer_reply_followup",
    label: "Customer reply follow-up",
    category: "communication",
    defaultPriority: "urgent",
    manualCreatable: false,
    defaultFollowUpType: "follow_up",
    completionOutcomes: COMMUNICATION_OUTCOMES,
  },
  consultation_followup: {
    type: "consultation_followup",
    label: "Consultation follow-up",
    category: "sales",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "scheduled_call",
    completionOutcomes: COMMUNICATION_OUTCOMES,
    completionFields: CALL_COMPLETION_FIELDS,
  },
  offer_creation: {
    type: "offer_creation",
    label: "Offer creation",
    category: "offer",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "offer_followup",
    completionOutcomes: OFFER_OUTCOMES,
    completionFields: OFFER_COMPLETION_FIELDS,
  },
  offer_followup: {
    type: "offer_followup",
    label: "Offer follow-up",
    category: "offer",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "offer_followup",
    completionOutcomes: OFFER_OUTCOMES,
    completionFields: OFFER_COMPLETION_FIELDS,
  },
  proposal_revival: {
    type: "proposal_revival",
    label: "Proposal revival",
    category: "offer",
    defaultPriority: "high",
    manualCreatable: false,
    defaultFollowUpType: "offer_followup",
    completionOutcomes: OFFER_OUTCOMES,
    completionFields: OFFER_COMPLETION_FIELDS,
  },
  promise_followup: {
    type: "promise_followup",
    label: "Promise follow-up",
    category: "offer",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "payment_followup",
    completionOutcomes: OFFER_OUTCOMES,
    completionFields: OFFER_COMPLETION_FIELDS,
  },
  payment_followup: {
    type: "payment_followup",
    label: "Payment follow-up",
    category: "payment",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "payment_followup",
    completionOutcomes: OFFER_OUTCOMES,
    completionFields: OFFER_COMPLETION_FIELDS,
  },
  payment_verification: {
    type: "payment_verification",
    label: "Payment verification",
    category: "payment",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "payment_followup",
    completionOutcomes: OFFER_OUTCOMES,
    completionFields: PAYMENT_VERIFICATION_FIELDS,
  },
  invoice_collection: {
    type: "invoice_collection",
    label: "Invoice collection",
    category: "payment",
    defaultPriority: "urgent",
    manualCreatable: true,
    defaultFollowUpType: "payment_followup",
    completionOutcomes: OFFER_OUTCOMES,
    completionFields: OFFER_COMPLETION_FIELDS,
  },
  plan_classification: {
    type: "plan_classification",
    label: "Plan classification",
    category: "sales",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: ["completed", "customer_replied", "escalated", "cancelled"],
    completionFields: PLAN_CLASSIFICATION_FIELDS,
  },
  renewal: {
    type: "renewal",
    label: "Renewal",
    category: "retention",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "renewal",
    completionOutcomes: RETENTION_OUTCOMES,
    completionFields: RETENTION_COMPLETION_FIELDS,
  },
  subscription_renewal: {
    type: "subscription_renewal",
    label: "Subscription renewal",
    category: "retention",
    defaultPriority: "high",
    manualCreatable: false,
    defaultFollowUpType: "renewal",
    completionOutcomes: RETENTION_OUTCOMES,
    completionFields: RETENTION_COMPLETION_FIELDS,
  },
  engagement_check: {
    type: "engagement_check",
    label: "Engagement check",
    category: "retention",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: RETENTION_OUTCOMES,
    completionFields: RETENTION_COMPLETION_FIELDS,
  },
  cadence_outreach: {
    type: "cadence_outreach",
    label: "Cadence outreach",
    category: "communication",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: COMMUNICATION_OUTCOMES,
    completionFields: CADENCE_COMPLETION_FIELDS,
  },
  winback: {
    type: "winback",
    label: "Win-back",
    category: "retention",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "call",
    completionOutcomes: RETENTION_OUTCOMES,
    completionFields: RETENTION_COMPLETION_FIELDS,
  },
  churn_prevention: {
    type: "churn_prevention",
    label: "Churn prevention",
    category: "retention",
    defaultPriority: "high",
    manualCreatable: false,
    defaultFollowUpType: "call",
    completionOutcomes: RETENTION_OUTCOMES,
    completionFields: RETENTION_COMPLETION_FIELDS,
  },
  break_return_check: {
    type: "break_return_check",
    label: "Break return check",
    category: "retention",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "scheduled_call",
    completionOutcomes: RETENTION_OUTCOMES,
    completionFields: BREAK_RETURN_COMPLETION_FIELDS,
  },
  no_show_followup: {
    type: "no_show_followup",
    label: "No-show follow-up",
    category: "service",
    defaultPriority: "medium",
    manualCreatable: false,
    defaultFollowUpType: "scheduled_call",
    completionOutcomes: SERVICE_OUTCOMES,
    completionFields: SERVICE_COMPLETION_FIELDS,
  },
  support_ticket: {
    type: "support_ticket",
    label: "Support ticket",
    category: "service",
    defaultPriority: "high",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: SERVICE_OUTCOMES,
    completionFields: SERVICE_COMPLETION_FIELDS,
  },
  email_bounce_followup: {
    type: "email_bounce_followup",
    label: "Email bounce recovery",
    category: "communication",
    defaultPriority: "high",
    manualCreatable: false,
    defaultFollowUpType: "call",
    completionOutcomes: COMMUNICATION_OUTCOMES,
  },
  deal_revival: {
    type: "deal_revival",
    label: "Deal revival",
    category: "sales",
    defaultPriority: "medium",
    manualCreatable: false,
    defaultFollowUpType: "follow_up",
    completionOutcomes: COMMUNICATION_OUTCOMES,
  },
  review: {
    type: "review",
    label: "Review",
    category: "quality",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: ["completed", "escalated", "cancelled"],
  },
  review_recovery: {
    type: "review_recovery",
    label: "Review recovery",
    category: "quality",
    defaultPriority: "urgent",
    manualCreatable: false,
    defaultFollowUpType: "follow_up",
    completionOutcomes: ["completed", "customer_replied", "escalated", "cancelled"],
  },
  feedback_milestone: {
    type: "feedback_milestone",
    label: "Feedback milestone",
    category: "quality",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: ["completed", "customer_replied", "escalated", "cancelled"],
    completionFields: FEEDBACK_COMPLETION_FIELDS,
  },
  referral_followup: {
    type: "referral_followup",
    label: "Referral follow-up",
    category: "sales",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: COMMUNICATION_OUTCOMES,
  },
  learning_plateau_intervention: {
    type: "learning_plateau_intervention",
    label: "Learning plateau intervention",
    category: "learning",
    defaultPriority: "high",
    manualCreatable: false,
    defaultFollowUpType: "consultation_followup",
    completionOutcomes: SERVICE_OUTCOMES,
    completionFields: SERVICE_COMPLETION_FIELDS,
  },
  learning_streak_recovery: {
    type: "learning_streak_recovery",
    label: "Learning streak recovery",
    category: "learning",
    defaultPriority: "medium",
    manualCreatable: false,
    defaultFollowUpType: "follow_up",
    completionOutcomes: SERVICE_OUTCOMES,
    completionFields: SERVICE_COMPLETION_FIELDS,
  },
  custom: {
    type: "custom",
    label: "Custom",
    category: "custom",
    defaultPriority: "medium",
    manualCreatable: true,
    defaultFollowUpType: "follow_up",
    completionOutcomes: ["completed", "rescheduled", "cancelled", "escalated"],
  },
};

export const CRM_TASK_REGISTRY_OPTIONS = CRM_TASK_TYPE_VALUES.map((type) => CRM_TASK_REGISTRY[type]);

export function isCrmTaskType(value: string): value is CrmTaskType {
  return (CRM_TASK_TYPE_VALUES as readonly string[]).includes(value);
}

function toTaskToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s.-]+/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function normalizeCrmTaskType(value: string | null | undefined): CrmTaskType | null {
  if (!value) return null;
  if (isCrmTaskType(value)) return value;
  const token = toTaskToken(value);
  if (isCrmTaskType(token)) return token;
  return CRM_TASK_TYPE_ALIASES[token as CrmTaskTypeAlias] ?? null;
}

export function expandCrmTaskTypeFilterValues(value: string | null | undefined): string[] {
  if (!value) return [];
  const raw = value.trim();
  const token = toTaskToken(raw);
  const canonicalType = normalizeCrmTaskType(raw);
  const values = new Set<string>();

  if (raw) values.add(raw);
  if (token) values.add(token);
  if (canonicalType) {
    values.add(canonicalType);
    for (const [alias, mappedType] of Object.entries(CRM_TASK_TYPE_ALIASES)) {
      if (mappedType === canonicalType) values.add(alias);
    }
  }

  return Array.from(values);
}

export function getCrmTaskDefinition(type: string | null | undefined): CrmTaskRegistryEntry {
  const canonicalType = normalizeCrmTaskType(type);
  return canonicalType ? CRM_TASK_REGISTRY[canonicalType] : CRM_TASK_REGISTRY.custom;
}

export function isManualCreatableCrmTaskType(type: string | null | undefined): boolean {
  return getCrmTaskDefinition(type).manualCreatable;
}

export function getCrmTaskCompletionOutcomeDefinitions(
  type: string | null | undefined,
): CrmTaskOutcomeDefinition[] {
  return getCrmTaskDefinition(type).completionOutcomes.map((outcome) => CRM_TASK_OUTCOME_DEFINITIONS[outcome]);
}
